import { Link } from '@inertiajs/react'
import { Page, PageHeader } from '@/components/page'
import { WizardLayout } from '../Wizard/WizardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { FormationEntity } from '@/types'
import { CheckCircle2, Clock } from 'lucide-react'

interface Payment {
  id: string
  service_fee_cents: number
  state_fee_cents: number
  total_cents: number
  currency: string
  paid: boolean
  paid_at: string | null
  stripe_checkout_session_id: string | null
  line_items: Array<{
    line_item_type: string
    description: string
    amount_cents: number
  }>
  invoice?: {
    number: string
    issued_at: string
  }
  receipt?: {
    number: string
    issued_at: string
    payment_method: string
  }
}

type Props = {
  payment: Payment | null
  formation: { id: string; legal_name: string; entity: FormationEntity }
}

export default function PaymentShow({ payment, formation }: Props) {
  const basePath = useBasePath()
  const paymentCancelled = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('payment') === 'cancel'

  return (
    <WizardLayout
      title={`Payment — ${formation.legal_name}`}
      currentStep="pay"
      formationId={formation.id}
      resumeStep="pay"
      editable={false}
      breadcrumbs={[
        { label: 'Businesses', href: 'businesses' },
        { label: formation.legal_name, href: `formations/${formation.id}` },
        { label: 'Payment' }
      ]}
    >
      <Page>
        <PageHeader
          title={<>Payment</>}
          badge={payment && <Badge variant="secondary" className="font-normal">{payment.paid ? 'Paid' : 'Pending'}</Badge>}
          subtitle={
            <p className="text-sm text-muted-foreground">{formation.legal_name}</p>
          }
        />

        {paymentCancelled && (
          <div className="border border-dashed rounded-lg p-4 mb-6 bg-muted/30">
            <p className="text-sm font-medium text-foreground">Payment cancelled</p>
            <p className="text-xs text-muted-foreground mt-1">You can try again when you're ready.</p>
          </div>
        )}

        {!payment ? (
          <div className="space-y-6">
            <section className="border rounded-lg">
              <div className="px-4 py-3 border-b">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Summary</h2>
              </div>
              <div className="px-4 py-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Service Fee</span>
                  <span className="text-foreground">{formatCurrency(formation.entity.service_fee_cents)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{formation.entity.name} Filing Fee</span>
                  <span className="text-foreground">{formatCurrency(formation.entity.state_fee_cents)}</span>
                </div>
                <div className="h-px bg-border my-1.5" />
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-foreground">Total</span>
                  <span className="text-foreground">{formatCurrency(formation.entity.total_fee_cents)}</span>
                </div>
              </div>
            </section>
          </div>
        ) : payment.paid ? (
          <div className="space-y-6">
            <section className="border rounded-lg">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Payment complete</h2>
                <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                  <CheckCircle2 className="w-3 h-3" />
                  Paid on {new Date(payment.paid_at!).toLocaleDateString()}
                </span>
              </div>
              <div className="px-4 py-3 space-y-2">
                {payment.line_items.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{item.description}</span>
                    <span className="text-foreground">{formatCurrency(item.amount_cents)}</span>
                  </div>
                ))}
                <div className="h-px bg-border my-1.5" />
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-foreground">Total Paid</span>
                  <span className="text-foreground">{formatCurrency(payment.total_cents)}</span>
                </div>
                {payment.receipt && (
                  <div className="flex justify-between text-xs text-muted-foreground pt-1">
                    <span>Receipt</span>
                    <span className="font-mono">{payment.receipt.number}</span>
                  </div>
                )}
                {payment.invoice && (
                  <div className="flex justify-between text-xs text-muted-foreground pt-0.5">
                    <span>Invoice</span>
                    <span className="font-mono">{payment.invoice.number}</span>
                  </div>
                )}
              </div>
            </section>

            <Button asChild variant="outline" className="w-full">
              <Link href={`${basePath}/formations/${formation.id}`}>Back to Formation</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <section className="border rounded-lg">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Payment pending</h2>
                <span className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                  <Clock className="w-3 h-3" />
                  Payment not completed
                </span>
              </div>
              <div className="px-4 py-3 space-y-2">
                {payment.line_items.map((item, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{item.description}</span>
                    <span className="text-foreground">{formatCurrency(item.amount_cents)}</span>
                  </div>
                ))}
                <div className="h-px bg-border my-1.5" />
                <div className="flex justify-between text-sm font-medium">
                  <span className="text-foreground">Total</span>
                  <span className="text-foreground">{formatCurrency(payment.total_cents)}</span>
                </div>
              </div>
            </section>
          </div>
        )}
      </Page>
    </WizardLayout>
  )
}
